import React from 'react';
import { CheckCircle2, Info } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export interface PfuResult {
  category_name: string; mrp_value: number; amount_mrp: number;
  user_pd: number; user_pun: number; user_pfot: number;
  real_pd: number; real_pun: number; real_pfot: number;
  eff_pd: number; eff_pun: number; eff_pfot: number; eff_pfu: number;
  tier: { max_mrp: number | null; req_pd: number; req_pun: number; req_pfot: number; req_pfu: number };
  passes: boolean;
}

/** Shared result presentation for the real calculator and its fixed public examples. */
export default function PfuResultCard({ result }: { result: PfuResult }) {
  const { t, language } = useLanguage();
  const kk = language === 'kk';
  const fmt = (value: number) => new Intl.NumberFormat(kk ? 'kk-KZ' : 'ru-RU', { maximumFractionDigits: 2 }).format(value);
  const Icon = result.passes ? CheckCircle2 : Info;
  return <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" aria-label={t('calc.resultScore')}>
    <div className="flex flex-wrap items-center justify-between gap-5 border-b border-slate-200 p-5 sm:p-6">
      <div className="flex min-w-0 items-start gap-3">
        <Icon className={`mt-1 h-5 w-5 shrink-0 ${result.passes ? 'text-emerald-700' : 'text-amber-700'}`} aria-hidden="true" />
        <div><h2 className="text-base font-semibold text-slate-900">{result.passes ? t('calc.resultPass') : t('calc.resultFail')}</h2>
          <p className="mt-1 text-sm text-slate-500">{result.category_name}</p></div>
      </div>
      <div className="shrink-0"><p className="text-xs font-medium text-slate-500">{t('calc.resultScore')}</p>
        <p className="mt-1 flex items-baseline gap-2 tabular-nums"><strong className={`text-3xl font-semibold ${result.passes ? 'text-emerald-700' : 'text-amber-700'}`}>{fmt(result.eff_pfu)}</strong><span className="text-sm text-slate-500">/ {fmt(result.tier.req_pfu)}</span></p>
      </div>
    </div>
    <div className="p-5 sm:p-6">
      <dl className="divide-y divide-slate-100">
        {[
          { label: t('calc.resultPd'), value: result.eff_pd, maximum: result.tier.req_pd },
          { label: t('calc.resultPun'), value: result.eff_pun, maximum: result.tier.req_pun },
          { label: t('calc.resultPfot'), value: result.eff_pfot, maximum: result.tier.req_pfot },
        ].map(row => <div key={row.label} className="flex items-center justify-between gap-4 py-3 first:pt-0">
          <dt className="text-sm text-slate-600">{row.label}</dt><dd className="shrink-0 text-sm tabular-nums"><strong className="font-semibold text-slate-900">{fmt(row.value)}</strong><span className="ml-1 text-slate-500">/ {fmt(row.maximum)}</span></dd>
        </div>)}
      </dl>
      <p className="mt-3 text-sm leading-6 text-slate-600">{t('calc.resultExplanation')}</p>
      <div className="mt-5 grid gap-4 border-t border-slate-200 pt-4 text-xs sm:grid-cols-2">
        <p className="text-slate-500">{t('calc.resultAmountMrp')}<strong className="mt-1 block font-medium tabular-nums text-slate-800">{fmt(result.amount_mrp)} {kk ? 'АЕК' : 'МРП'}</strong></p>
        <p className="text-slate-500">{t('calc.resultTierMax')}<strong className="mt-1 block font-medium tabular-nums text-slate-800">{result.tier.max_mrp === null ? (kk ? 'Шектеусіз' : 'Без ограничения') : `${fmt(result.tier.max_mrp)} ${kk ? 'АЕК' : 'МРП'}`}</strong></p>
      </div>
    </div>
  </section>;
}
